import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchWorkspaceDetails,
  selectCurrentWorkspace,
  selectUnfiledTopics,
  selectDueTopics,
  DUE_DAILY_CAP,
  setWorkspaceLoaded,
  addTopic,
  updateBasket,
  connectionsReturnSaved,
  connectionsReturnCleared,
} from '../../store/workspacesSlice';
import { store } from '../../store';
import { getConnectionsSearchResult, HUB_SIZE_CAP } from '../../lib/basketSearch';
import { getWorkspaceDetailPath } from '../../lib/resolveMostRecentWorkspace';
import Searchbar from '../BasketSelectionInterface/components/Searchbar';
import AddItemModal from '../AddItemModal/AddItemModal';
import BasketSelectionInterface from '../BasketSelectionInterface/BasketSelectionInterface';
import ErrorBoundary from '../ErrorBoundary';
import CreateWorkspaceModal from '../CreateWorkspaceModal';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import ConnectionsDefaultHome from './ConnectionsDefaultHome';
import EmptyWorkspace from './EmptyWorkspace';
import ConnectionsZeroMatch from './ConnectionsZeroMatch';
import ConnectionsSearchResults from './ConnectionsSearchResults';
import ConnectionsExpandedBasketCard from './ConnectionsExpandedBasketCard';
import ConnectionsExpandedStandaloneCard from './ConnectionsExpandedStandaloneCard';
import LearnLayout from './LearnLayout';
import { usePremium } from '../Subscription/PremiumProvider';
import { isLocked } from '../../lib/premiumLock';
import { filterBasketsByMastery, MASTERY_FILTER_OPTIONS } from './masteryFilter';
import { orderBasketsForRow, pickStartTopicId } from './basketRow';

const CONNECTIONS_RETURN_TTL_MS = 15 * 60 * 1000;

const peekConnectionsReturn = (workspaceId) => {
  const saved = store.getState()?.workspaces?.connectionsReturn;
  if (!saved || saved.workspaceId !== workspaceId) return null;
  if (typeof saved.savedAt !== 'number') return null;
  if (Date.now() - saved.savedAt > CONNECTIONS_RETURN_TTL_MS) return null;
  return saved;
};

const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Connections — workspace home (v6): search-first default with Baskets/Unfiled,
 * two-tier results, Learn sidebar.
 */
const ConnectionsPage = () => {
  const { workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    baskets,
    topics,
    status,
    error,
    isWorkspaceLoaded,
    id: loadedWorkspaceId,
  } = useSelector(selectCurrentWorkspace);
  const isReady = isWorkspaceLoaded && loadedWorkspaceId === workspaceId;
  const unfiledTopics = useSelector(selectUnfiledTopics);
  const dueTopicsRaw = useSelector(selectDueTopics);
  const { isPremium, isLoading: isPremiumLoading } = usePremium();
  const masteryPremiumOpts = useMemo(
    () => ({ isPremium, isLoading: isPremiumLoading }),
    [isPremium, isPremiumLoading]
  );
  const dueTopics = useMemo(
    () =>
      (dueTopicsRaw || []).filter(
        (topic) => !isLocked(topic, isPremium, isPremiumLoading)
      ),
    [dueTopicsRaw, isPremium, isPremiumLoading]
  );

  const workspaceName = location.state?.workspaceName;

  // Restore from connectionsReturn once on mount (cleared in an effect below).
  const [searchTerm, setSearchTerm] = useState(
    () => peekConnectionsReturn(workspaceId)?.searchTerm ?? ''
  );
  const [openedBasketId, setOpenedBasketId] = useState(
    () => peekConnectionsReturn(workspaceId)?.openedBasketId ?? null
  );
  const [openedBasketSpokeId, setOpenedBasketSpokeId] = useState(
    () => peekConnectionsReturn(workspaceId)?.openedBasketSpokeId ?? null
  );
  const [openedStandaloneTopicId, setOpenedStandaloneTopicId] = useState(
    () => peekConnectionsReturn(workspaceId)?.openedStandaloneTopicId ?? null
  );
  const [dueDoneIds, setDueDoneIds] = useState(() => new Set());
  const [dueRoundKey, setDueRoundKey] = useState(0);
  // Opened basket/standalone/search render above default home; landing keeps that path.
  const [defaultView, setDefaultView] = useState('landing');
  /** null = auto-resolve (due if any, else baskets) until user picks a tab */
  const [defaultTab, setDefaultTab] = useState(
    () => peekConnectionsReturn(workspaceId)?.tab ?? null
  );
  const [unfiledTagFilter, setUnfiledTagFilter] = useState('all');
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [addItemPrefill, setAddItemPrefill] = useState('');
  const [addItemBasket, setAddItemBasket] = useState(null);
  const [basketSelectionTopic, setBasketSelectionTopic] = useState(null);
  const [isCreateWorkspaceOpen, setIsCreateWorkspaceOpen] = useState(false);
  const [masteryFilter, setMasteryFilter] = useState(
    () => peekConnectionsReturn(workspaceId)?.masteryFilter ?? null
  );
  const [isRetryingLoad, setIsRetryingLoad] = useState(false);
  // Restored tab counts as user-picked so autoTab does not override it.
  const [autoTab, setAutoTab] = useState(() => {
    const tab = peekConnectionsReturn(workspaceId)?.tab;
    return tab != null ? tab : null;
  });
  const [basketRowOrderIds, setBasketRowOrderIds] = useState(null);
  /** 'row' = tile/auto-open (row stays, no Back); 'manual' = All baskets / restore (Back). */
  // Restore returns to landing + row (not All-baskets Back mode).
  const [basketOpenSource, setBasketOpenSource] = useState(() =>
    peekConnectionsReturn(workspaceId)?.openedBasketId ? 'row' : null
  );

  const resolvedDefaultTab = defaultTab ?? autoTab ?? 'baskets';
  const prevWorkspaceIdRef = useRef(null);
  const basketRowOrderedForWorkspaceRef = useRef(null);

  useEffect(() => {
    if (workspaceId && (!isWorkspaceLoaded || loadedWorkspaceId !== workspaceId)) {
      dispatch(setWorkspaceLoaded(false));
      dispatch(fetchWorkspaceDetails(workspaceId));
    }
  }, [workspaceId, dispatch, isWorkspaceLoaded, loadedWorkspaceId]);

  // Clear any saved return snapshot after applying (or rejecting) it on mount.
  useEffect(() => {
    const saved = store.getState()?.workspaces?.connectionsReturn;
    if (!saved) return;
    dispatch(connectionsReturnCleared());
  }, [dispatch]);

  // Reset local UI when switching workspaces (same route, no remount).
  // Skip the first run so a restored connectionsReturn is not wiped on mount.
  useEffect(() => {
    if (prevWorkspaceIdRef.current === null) {
      prevWorkspaceIdRef.current = workspaceId;
      return;
    }
    if (prevWorkspaceIdRef.current === workspaceId) return;
    prevWorkspaceIdRef.current = workspaceId;

    setSearchTerm('');
    setOpenedBasketId(null);
    setOpenedBasketSpokeId(null);
    setOpenedStandaloneTopicId(null);
    setDueDoneIds(new Set());
    setDueRoundKey(0);
    setMasteryFilter(null);
    setDefaultView('landing');
    setDefaultTab(null);
    setAutoTab(null);
    setUnfiledTagFilter('all');
    setBasketRowOrderIds(null);
    basketRowOrderedForWorkspaceRef.current = null;
    setBasketOpenSource(null);
  }, [workspaceId]);

  useEffect(() => {
    if (!isReady || autoTab !== null) return;
    setAutoTab((dueTopics?.length ?? 0) > 0 ? 'due' : 'baskets');
  }, [isReady, autoTab, dueTopics?.length]);

  // Fire once per workspace open
  useEffect(() => {
    if (!workspaceId || !isReady) return;

    const source = location.state?.workspaceOpenSource || 'default_resume';

    captureConnectionsEvent('workspace_opened', {
      workspace_id: workspaceId,
      source,
    });
  }, [workspaceId, isReady]); // eslint-disable-line react-hooks/exhaustive-deps -- fire once when loaded, not on every location.state change

  const searchResult = useMemo(
    () => getConnectionsSearchResult(baskets || [], unfiledTopics || [], searchTerm),
    [baskets, unfiledTopics, searchTerm]
  );

  const filteredDefaultBaskets = useMemo(
    () => filterBasketsByMastery(baskets, masteryFilter, masteryPremiumOpts),
    [baskets, masteryFilter, masteryPremiumOpts]
  );

  // Order baskets once per workspace load; optionally default-open the first.
  useEffect(() => {
    if (!isReady || isPremiumLoading) return;
    if (basketRowOrderedForWorkspaceRef.current === workspaceId) return;
    basketRowOrderedForWorkspaceRef.current = workspaceId;

    const ordered = orderBasketsForRow(baskets || [], {
      isPremium,
      isLoading: isPremiumLoading,
    });
    const ids = ordered.map((basket) => basket.id);
    setBasketRowOrderIds(ids);

    captureConnectionsEvent('basket_row_shown', {
      workspace_id: workspaceId,
      first_basket_id: ids[0] || null,
      row_size: ids.length,
    });

    // Restored open wins; mastery filter: no auto-open.
    if (openedBasketId || masteryFilter) return;
    if (ids.length === 0) return;

    const first = ordered[0];
    const canDefaultOpen =
      (first?.topics?.length || 0) >= 2 &&
      !isLocked(first, isPremium, isPremiumLoading);
    if (!canDefaultOpen) return;

    setOpenedBasketId(first.id);
    setOpenedBasketSpokeId(pickStartTopicId(first));
    setBasketOpenSource('row');
  }, [
    isReady,
    isPremiumLoading,
    workspaceId,
    baskets,
    isPremium,
    openedBasketId,
    masteryFilter,
  ]);

  // Keep the frozen row in sync with Redux: append new baskets, drop deleted.
  // Do not re-run orderBasketsForRow (would reshuffle free-user mix / first slot).
  useEffect(() => {
    if (!isReady) return;
    if (basketRowOrderedForWorkspaceRef.current !== workspaceId) return;
    if (basketRowOrderIds === null) return;

    const liveIds = (baskets || []).map((basket) => basket.id);
    const liveIdSet = new Set(liveIds);
    const orderedIdSet = new Set(basketRowOrderIds);

    const pruned = basketRowOrderIds.filter((id) => liveIdSet.has(id));
    const added = liveIds.filter((id) => !orderedIdSet.has(id));
    if (pruned.length === basketRowOrderIds.length && added.length === 0) {
      return;
    }

    // Newest first so a just-created basket lands in the glance strip.
    setBasketRowOrderIds([...added, ...pruned]);
  }, [baskets, basketRowOrderIds, isReady, workspaceId]);

  // BSI returns with activeTab: 'Baskets' after a successful place — show the
  // Baskets tab and open the target basket when provided.
  useEffect(() => {
    const tab = location.state?.activeTab;
    if (tab !== 'Baskets' && tab !== 'baskets') return;

    setDefaultTab('baskets');
    setAutoTab('baskets');
    setDefaultView('landing');
    setSearchTerm('');

    const openBasketId = location.state?.shouldScrollToBasket;
    if (openBasketId) {
      const basket = (baskets || []).find((item) => item.id === openBasketId);
      setOpenedBasketId(openBasketId);
      setOpenedBasketSpokeId(basket ? pickStartTopicId(basket) : null);
      setOpenedStandaloneTopicId(null);
      setBasketOpenSource('row');
    }

    const { activeTab: _tab, shouldScrollToBasket: _scroll, ...rest } =
      location.state || {};
    navigate(location.pathname, { replace: true, state: rest });
  }, [location.state, location.pathname, navigate, baskets]);

  const trimmedSearchTerm = searchTerm.trim();

  // Debounced search analytics (results or zero — not empty query)
  useEffect(() => {
    if (!trimmedSearchTerm || !workspaceId) return undefined;
    if (searchResult.type !== 'results' && searchResult.type !== 'zero') {
      return undefined;
    }

    const timer = setTimeout(() => {
      captureConnectionsEvent('connections_search_used', {
        query: trimmedSearchTerm,
        tier1_count: searchResult.type === 'results' ? searchResult.tier1.length : 0,
        tier2_count: searchResult.type === 'results' ? searchResult.tier2.length : 0,
        workspace_id: workspaceId,
      });
    }, 600);

    return () => clearTimeout(timer);
  }, [trimmedSearchTerm, searchResult, workspaceId]);

  const hasTopics = (topics?.length ?? 0) > 0;

  // Default landing visible. The empty teaching screen is not that list.
  useEffect(() => {
    if (!workspaceId || !isWorkspaceLoaded) return;
    if (!hasTopics) return;
    if (searchResult.type !== 'empty') return;
    if (openedBasketId || openedStandaloneTopicId) return;
    if (defaultView !== 'landing') return;

    captureConnectionsEvent('connections_default_list_opened', {
      workspace_id: workspaceId,
    });
  }, [
    workspaceId,
    isWorkspaceLoaded,
    hasTopics,
    searchResult.type,
    openedBasketId,
    openedStandaloneTopicId,
    defaultView,
  ]);

  const openedBasket = useMemo(() => {
    if (!openedBasketId) return null;
    return (baskets || []).find((b) => b.id === openedBasketId) || null;
  }, [openedBasketId, baskets]);

  const openedBasketItem = useMemo(() => {
    if (!openedBasket) return null;
    const basketTopics = openedBasket.topics || [];
    return {
      kind: 'basket',
      id: openedBasket.id,
      name: openedBasket.name,
      updatedAt: openedBasket.updated_at || null,
      topics: basketTopics,
      matchedTopicId: openedBasketSpokeId,
      oversized: basketTopics.length > HUB_SIZE_CAP,
    };
  }, [openedBasket, openedBasketSpokeId]);

  // Lookup from full topics list so Due (basketed) and Unfiled both resolve
  const openedStandaloneTopic = useMemo(() => {
    if (!openedStandaloneTopicId) return null;
    return (topics || []).find((t) => t.id === openedStandaloneTopicId) || null;
  }, [openedStandaloneTopicId, topics]);

  const openedStandaloneItem = useMemo(() => {
    if (!openedStandaloneTopic) return null;
    return {
      kind: 'standalone',
      id: openedStandaloneTopic.id,
      name: openedStandaloneTopic.name,
      updatedAt: openedStandaloneTopic.updated_at || null,
      topic: openedStandaloneTopic,
    };
  }, [openedStandaloneTopic]);

  const handleBeforeLeave = (topic) => {
    if (!workspaceId) return;
    // Prefer the departing topic so the selected spoke survives even when
    // openedBasketSpokeId was never written (local hub selection).
    const nextSpokeId = topic?.basket_id ? topic.id : openedBasketSpokeId;
    const nextStandaloneId =
      topic && !topic.basket_id ? topic.id : openedStandaloneTopicId;

    dispatch(
      connectionsReturnSaved({
        workspaceId,
        tab: resolvedDefaultTab,
        openedBasketId,
        openedBasketSpokeId: nextSpokeId,
        openedStandaloneTopicId: nextStandaloneId,
        searchTerm,
        masteryFilter,
        savedAt: Date.now(),
      })
    );
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setOpenedBasketId(null);
    setOpenedBasketSpokeId(null);
    setOpenedStandaloneTopicId(null);
    setBasketOpenSource(null);
    setDefaultView('landing');
  };

  const handleSearchTermChange = (value) => {
    setSearchTerm(value);
    setOpenedBasketId(null);
    setOpenedBasketSpokeId(null);
    setOpenedStandaloneTopicId(null);
    setBasketOpenSource(null);
    setDefaultView('landing');
  };

  const handleBasketOpen = (basket) => {
    setOpenedStandaloneTopicId(null);
    setOpenedBasketSpokeId(null);
    setOpenedBasketId(basket.id);
    setBasketOpenSource('manual');
  };

  const handleBasketRowTileOpen = (basket, position) => {
    if (!basket?.id) return;
    setOpenedStandaloneTopicId(null);
    setOpenedBasketId(basket.id);
    setOpenedBasketSpokeId(pickStartTopicId(basket));
    setBasketOpenSource('row');
    captureConnectionsEvent('basket_row_tile_opened', {
      basket_id: basket.id,
      position,
    });
  };

  const handleTogglePin = (basket) => {
    const nextPinned = !basket.is_pinned;
    dispatch(
      updateBasket({
        basketId: basket.id,
        updatedData: { is_pinned: nextPinned },
        workspaceId,
      })
    );
    captureConnectionsEvent(nextPinned ? 'basket_pinned' : 'basket_unpinned', {
      basket_id: basket.id,
      workspace_id: workspaceId,
    });
  };

  const handleUnfiledOpen = (topic) => {
    setOpenedBasketId(null);
    setOpenedBasketSpokeId(null);
    setBasketOpenSource(null);
    setOpenedStandaloneTopicId(topic.id);
  };

  const handleDueTopicDone = (id) => {
    setDueDoneIds((prev) => new Set(prev).add(id));
  };

  const handleStartNextDueRound = (kind) => {
    const nextCount = Math.min(dueTopics.length, DUE_DAILY_CAP);
    setDueDoneIds(new Set());
    setDueRoundKey((key) => key + 1);
    captureConnectionsEvent('due_round_next_clicked', {
      kind,
      count: nextCount,
    });
  };

  const handleCloseOpenedBasket = () => {
    setOpenedBasketId(null);
    setOpenedBasketSpokeId(null);
    setBasketOpenSource(null);
  };

  const handleCloseOpenedStandalone = () => {
    setOpenedStandaloneTopicId(null);
  };

  const handleAddToBasket = (topic) => {
    setBasketSelectionTopic(topic);
  };

  const handleTopicDeleted = (topic) => {
    if (!topic?.id) return;
    if (openedBasketSpokeId === topic.id) {
      setOpenedBasketSpokeId(null);
    }
    if (openedStandaloneTopicId === topic.id) {
      setOpenedStandaloneTopicId(null);
    }
  };

  /** Re-open the first ordered row basket (default landing open). */
  const openFirstRowBasket = () => {
    if (masteryFilter) {
      setOpenedBasketId(null);
      setOpenedBasketSpokeId(null);
      setBasketOpenSource(null);
      return;
    }
    const byId = new Map((baskets || []).map((basket) => [basket.id, basket]));
    const first = (basketRowOrderIds || [])
      .map((id) => byId.get(id))
      .find(Boolean);
    if (!first) {
      setOpenedBasketId(null);
      setOpenedBasketSpokeId(null);
      setBasketOpenSource(null);
      return;
    }
    const canDefaultOpen =
      (first.topics?.length || 0) >= 2 &&
      !isLocked(first, isPremium, isPremiumLoading);
    if (!canDefaultOpen) {
      setOpenedBasketId(null);
      setOpenedBasketSpokeId(null);
      setBasketOpenSource(null);
      return;
    }
    setOpenedBasketId(first.id);
    setOpenedBasketSpokeId(pickStartTopicId(first));
    setBasketOpenSource('row');
  };

  const handleBackToLanding = () => {
    setDefaultView('landing');
    setOpenedStandaloneTopicId(null);
    setUnfiledTagFilter('all');
    // Always restore the default first row basket (not one opened from See all).
    openFirstRowBasket();
  };

  const handleSeeAll = (kind, source) => {
    const view = kind === 'unfiled' ? 'allUnfiled' : 'allBaskets';
    setDefaultView(view);
    setOpenedStandaloneTopicId(null);
    setOpenedBasketId(null);
    setOpenedBasketSpokeId(null);
    setBasketOpenSource(null);
    captureConnectionsEvent('connections_see_all_opened', {
      kind,
      source,
      workspace_id: workspaceId,
    });
  };

  const handleBasketsMasteryClick = () => {
    setSearchTerm('');
    setOpenedBasketId(null);
    setOpenedBasketSpokeId(null);
    setOpenedStandaloneTopicId(null);
    setBasketOpenSource(null);
    setDefaultView('allBaskets');
    setDefaultTab('baskets');
    captureConnectionsEvent('connections_see_all_opened', {
      kind: 'baskets',
      source: 'sidebar_stat',
      workspace_id: workspaceId,
    });
  };

  const handleUnfiledRemainingClick = () => {
    setSearchTerm('');
    setOpenedBasketId(null);
    setOpenedBasketSpokeId(null);
    setOpenedStandaloneTopicId(null);
    setBasketOpenSource(null);
    setDefaultView('allUnfiled');
    setDefaultTab('unfiled');
    setUnfiledTagFilter('all');
    captureConnectionsEvent('connections_see_all_opened', {
      kind: 'unfiled',
      source: 'sidebar_stat',
      workspace_id: workspaceId,
    });
  };

  const handleDefaultTabChange = (tab) => {
    setDefaultTab(tab);
    setOpenedStandaloneTopicId(null);
    // Keep openedBasketId / basketOpenSource across Due / Unfiled / Baskets.
  };

  const handleOpenCreateTopic = () => {
    setAddItemBasket(null);
    setAddItemPrefill(capitalizeFirstLetter(searchTerm.trim()));
    setIsAddItemModalOpen(true);
  };

  const handleQuickAdd = () => {
    setAddItemBasket(null);
    setAddItemPrefill('');
    setIsAddItemModalOpen(true);
  };

  const handleEmptyStateAddTopic = (text) => {
    setAddItemBasket(null);
    setAddItemPrefill(capitalizeFirstLetter((text || '').trim()));
    setIsAddItemModalOpen(true);
  };

  const handleAddItemToBasket = (basket, source = 'empty_state') => {
    if (!basket?.id) return;
    setAddItemBasket({ id: basket.id, name: basket.name });
    setAddItemPrefill('');
    setIsAddItemModalOpen(true);
    captureConnectionsEvent('basket_add_item_started', {
      basket_id: basket.id,
      workspace_id: workspaceId,
      source,
    });
  };

  const handleCloseAddItemModal = () => {
    setIsAddItemModalOpen(false);
    setAddItemBasket(null);
  };

  const handleMasteryFilterChange = (filterId) => {
    setMasteryFilter(filterId);
    if (filterId) {
      setOpenedBasketId(null);
      setOpenedBasketSpokeId(null);
      setBasketOpenSource(null);
    }
  };

  const masteryFilterLabel =
    MASTERY_FILTER_OPTIONS.find((option) => option.id === masteryFilter)
      ?.label || '';

  const handleNewWorkspace = () => {
    setIsCreateWorkspaceOpen(true);
  };

  const handleWorkspaceCreated = (newWorkspace) => {
    setIsCreateWorkspaceOpen(false);
    dispatch(setWorkspaceLoaded(false));
    navigate(getWorkspaceDetailPath(newWorkspace), {
      state: {
        workspaceName: newWorkspace.name,
        workspaceOpenSource: 'created',
      },
    });
  };

  const handleAddItem = async (newTopicData) => {
    try {
      const payload = newTopicData?.newTopic
        ? newTopicData
        : { workspaceId, newTopic: newTopicData };

      const createdTopic = await dispatch(addTopic(payload)).unwrap();

      // Pre-chosen basket: keep modal open for consecutive adds; skip the picker.
      if (payload.newTopic?.basket_id) {
        return;
      }

      setIsAddItemModalOpen(false);
      setAddItemBasket(null);
      setBasketSelectionTopic(createdTopic);
    } catch (addError) {
      console.error('Error adding item:', addError);
      const normalizedError =
        typeof addError === 'string' ? new Error(addError) : addError;
      throw normalizedError;
    }
  };

  const handleCloseBasketSelection = () => {
    setBasketSelectionTopic(null);
  };

  const handleRetryWorkspaceLoad = () => {
    if (isRetryingLoad) return;
    setIsRetryingLoad(true);
    dispatch(setWorkspaceLoaded(false));
    dispatch(fetchWorkspaceDetails(workspaceId)).finally(() => {
      setIsRetryingLoad(false);
    });
  };

  if (
    workspaceId &&
    (!isWorkspaceLoaded || loadedWorkspaceId !== workspaceId) &&
    !(status === 'failed' || error) &&
    !isRetryingLoad
  ) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-neutral-200 border-t-cranberry-600" />
      </div>
    );
  }

  if ((status === 'failed' || error || isRetryingLoad) && !isWorkspaceLoaded) {
    return (
      <LearnLayout
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        masteryFilter={masteryFilter}
        onMasteryFilterChange={handleMasteryFilterChange}
        onNewWorkspace={handleNewWorkspace}
        onQuickAdd={handleQuickAdd}
        onBasketsMasteryClick={handleBasketsMasteryClick}
        onUnfiledRemainingClick={handleUnfiledRemainingClick}
      >
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">
            Could not load this workspace
          </h2>
          <p className="text-sm text-neutral-600 mb-4">
            Check your connection, then try again.
          </p>
          <button
            type="button"
            onClick={handleRetryWorkspaceLoad}
            disabled={isRetryingLoad}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium hover:bg-cranberry-600 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cranberry-700"
          >
            {isRetryingLoad && (
              <span
                className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white"
                aria-hidden="true"
              />
            )}
            {isRetryingLoad ? 'Trying…' : 'Try again'}
          </button>
          <p className="mt-4 text-sm text-neutral-500">
            Or pick another workspace from the sidebar.
          </p>
        </div>
      </LearnLayout>
    );
  }

  const showOpenedBasket =
    searchResult.type === 'empty' && openedBasketItem && openedBasket;
  const isRowBasketMode = basketOpenSource === 'row';
  const showRowBasketCard =
    showOpenedBasket &&
    isRowBasketMode &&
    defaultView === 'landing' &&
    resolvedDefaultTab === 'baskets';
  const showManualBasketCard = showOpenedBasket && !isRowBasketMode;
  const showManualStandalone =
    searchResult.type === 'empty' && openedStandaloneItem && openedStandaloneTopic;
  const showDefaultHome =
    searchResult.type === 'empty' &&
    !showManualStandalone &&
    (!showOpenedBasket || isRowBasketMode);
  // Only after this workspace has finished loading, and only on the default
  // landing. All baskets stays available so empty baskets can be managed.
  const showEmptyLanding =
    isReady &&
    status !== 'loading' &&
    (topics?.length ?? 0) === 0 &&
    defaultView === 'landing' &&
    !showManualBasketCard &&
    !showManualStandalone;

  let feedbackView = 'Baskets';
  if (searchResult.type === 'results' || searchResult.type === 'zero') {
    feedbackView = 'Search';
  } else if (showManualBasketCard || showRowBasketCard || defaultView === 'allBaskets') {
    feedbackView = 'Baskets';
  } else if (showManualStandalone || defaultView === 'allUnfiled') {
    feedbackView = 'Unfiled';
  } else if (resolvedDefaultTab === 'due') {
    feedbackView = 'Due';
  } else if (resolvedDefaultTab === 'unfiled') {
    feedbackView = 'Unfiled';
  }

  // Next unlocked tile among the visible row (first 4 of ordered filtered ids).
  let handleNextRowBasket;
  if (showRowBasketCard && openedBasketId && basketRowOrderIds?.length) {
    const byId = new Map(
      (filteredDefaultBaskets || []).map((basket) => [basket.id, basket])
    );
    const rowTiles = basketRowOrderIds
      .map((id) => byId.get(id))
      .filter(Boolean)
      .slice(0, 4);
    const openIndex = rowTiles.findIndex((basket) => basket.id === openedBasketId);
    if (openIndex >= 0) {
      for (let position = openIndex + 1; position < rowTiles.length; position += 1) {
        const candidate = rowTiles[position];
        if (isLocked(candidate, isPremium, isPremiumLoading)) continue;
        handleNextRowBasket = () => handleBasketRowTileOpen(candidate, position);
        break;
      }
    }
  }

  return (
    <LearnLayout
      workspaceId={workspaceId}
      workspaceName={workspaceName}
      masteryFilter={masteryFilter}
      onMasteryFilterChange={handleMasteryFilterChange}
      onNewWorkspace={handleNewWorkspace}
      onQuickAdd={handleQuickAdd}
      feedbackView={feedbackView}
      onBasketsMasteryClick={handleBasketsMasteryClick}
      onUnfiledRemainingClick={handleUnfiledRemainingClick}
    >
      <div className="px-3 sm:px-6 py-4 sm:py-6 flex-1 flex flex-col gap-4">
        {showEmptyLanding ? (
          <EmptyWorkspace
            workspaceId={workspaceId}
            baskets={baskets || []}
            onAddFirstTopic={handleEmptyStateAddTopic}
          />
        ) : (
        <>
        <div className="w-full max-w-[700px]">
          <Searchbar
            searchTerm={searchTerm}
            setSearchTerm={handleSearchTermChange}
            onClearSearch={handleClearSearch}
            placeholder="What are you studying now?"
            onCreateItem={
              searchResult.type === 'results' &&
              searchResult.tier1.length === 0 &&
              searchResult.tier2.length > 0
                ? handleOpenCreateTopic
                : undefined
            }
          />
        </div>

        {showDefaultHome && (
          <ConnectionsDefaultHome
            defaultView={defaultView}
            defaultTab={resolvedDefaultTab}
            baskets={filteredDefaultBaskets}
            allBaskets={baskets || []}
            allTopics={topics || []}
            unfiledTopics={unfiledTopics || []}
            dueTopics={dueTopics || []}
            workspaceId={workspaceId}
            masteryFilterActive={Boolean(masteryFilter)}
            masteryFilterLabel={masteryFilterLabel}
            unfiledTagFilter={unfiledTagFilter}
            dueDoneIds={dueDoneIds}
            dueRoundKey={dueRoundKey}
            onDueTopicDone={handleDueTopicDone}
            onStartNextDueRound={handleStartNextDueRound}
            onDefaultTabChange={handleDefaultTabChange}
            onSeeAll={handleSeeAll}
            onBackToLanding={handleBackToLanding}
            onBasketOpen={handleBasketOpen}
            onTogglePin={handleTogglePin}
            onUnfiledOpen={handleUnfiledOpen}
            onUnfiledTagFilterChange={setUnfiledTagFilter}
            onClearMasteryFilter={() => handleMasteryFilterChange(null)}
            basketRowOrderIds={basketRowOrderIds}
            openedBasketId={openedBasketId}
            onBasketRowTileOpen={handleBasketRowTileOpen}
          />
        )}

        {showRowBasketCard && (
          <div className="w-full max-w-[700px]">
            <ConnectionsExpandedBasketCard
              key={openedBasketId}
              item={openedBasketItem}
              searchTerm=""
              onCollapse={handleCloseOpenedBasket}
              liveBasket={openedBasket}
              workspaceId={workspaceId}
              onTogglePin={handleTogglePin}
              onMoveToBasket={handleAddToBasket}
              onTopicDeleted={handleTopicDeleted}
              onBeforeLeave={handleBeforeLeave}
              onAddItemToBasket={handleAddItemToBasket}
              headerCollapsible={false}
              onNextBasket={handleNextRowBasket}
            />
          </div>
        )}

        {showManualBasketCard && (
          <div className="w-full max-w-[700px] space-y-3">
            <button
              type="button"
              onClick={handleCloseOpenedBasket}
              className="text-sm text-neutral-600 underline hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cranberry-700 rounded"
            >
              Back
            </button>
            <ConnectionsExpandedBasketCard
              key={openedBasketId}
              item={openedBasketItem}
              searchTerm=""
              onCollapse={handleCloseOpenedBasket}
              liveBasket={openedBasket}
              workspaceId={workspaceId}
              onTogglePin={handleTogglePin}
              onMoveToBasket={handleAddToBasket}
              onTopicDeleted={handleTopicDeleted}
              onBeforeLeave={handleBeforeLeave}
              onAddItemToBasket={handleAddItemToBasket}
            />
          </div>
        )}

        {showManualStandalone && (
          <div className="w-full max-w-[700px] space-y-3">
            <button
              type="button"
              onClick={handleCloseOpenedStandalone}
              className="text-sm text-neutral-600 underline hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cranberry-700 rounded"
            >
              Back
            </button>
            <ConnectionsExpandedStandaloneCard
              item={openedStandaloneItem}
              searchTerm=""
              onCollapse={handleCloseOpenedStandalone}
              liveTopic={openedStandaloneTopic}
              onAddToBasket={handleAddToBasket}
              onMoveToBasket={handleAddToBasket}
              onTopicDeleted={handleTopicDeleted}
              onBeforeLeave={handleBeforeLeave}
            />
          </div>
        )}

        {searchResult.type === 'zero' && (
          <ConnectionsZeroMatch
            query={searchTerm}
            onCreateTopic={handleOpenCreateTopic}
            onBackToList={handleClearSearch}
          />
        )}

        {searchResult.type === 'results' && (
          <ConnectionsSearchResults
            tier1={searchResult.tier1}
            tier2={searchResult.tier2}
            searchTerm={searchTerm.trim()}
            workspaceId={workspaceId}
            onTogglePin={handleTogglePin}
            onAddToBasket={handleAddToBasket}
            onMoveToBasket={handleAddToBasket}
            onTopicDeleted={handleTopicDeleted}
            onBeforeLeave={handleBeforeLeave}
            onAddItemToBasket={handleAddItemToBasket}
          />
        )}
        </>
        )}
      </div>

      <ErrorBoundary>
        {isAddItemModalOpen && (
          <AddItemModal
            isOpen={isAddItemModalOpen}
            onClose={handleCloseAddItemModal}
            onAdd={handleAddItem}
            workspaceId={workspaceId}
            defaultBasketId={addItemBasket?.id}
            basketName={addItemBasket?.name}
            initialTopicName={addItemPrefill}
          />
        )}
      </ErrorBoundary>

      <CreateWorkspaceModal
        isOpen={isCreateWorkspaceOpen}
        onClose={() => setIsCreateWorkspaceOpen(false)}
        onCreated={handleWorkspaceCreated}
      />

      {basketSelectionTopic && workspaceId && (
        <BasketSelectionInterface
          onClose={handleCloseBasketSelection}
          currentBasketId={basketSelectionTopic.basket_id}
          topic={basketSelectionTopic}
          workspaceId={workspaceId}
        />
      )}
    </LearnLayout>
  );
};

export default ConnectionsPage;
